import styled from 'styled-components';
import { UI } from '../constants';

// Common container styles
export const PageContainer = styled.div`
  padding: 2.5rem;
  max-width: 900px;
  margin: 0 auto;
  min-height: calc(100vh - 160px);
  position: relative;
  z-index: 1;
`;

export const SectionContainer = styled.div`
  margin-bottom: 1.5rem;
`;

export const SubSectionContainer = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

// Common text styles
export const SectionTitle = styled.h3<{ isOpen?: boolean }>`
  font-family: var(--font-family-display);
  color: var(--color-text-primary);
  margin: 0;
  font-size: var(--font-size-title);
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  &::before {
    content: "▶";
    font-size: 0.6rem;
    margin-right: 0.875rem;
    transform: ${props => props.isOpen ? 'rotate(90deg)' : 'rotate(0)'};
    transition: transform 0.2s ease;
    color: rgba(0, 0, 0, 0.4);
  }
`;

export const SubSectionTitle = styled.h4<{ isOpen?: boolean }>`
  font-family: var(--font-family-display);
  color: var(--color-text-secondary);
  margin: 0;
  font-size: var(--font-size-subtitle);
  font-weight: 500;
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  &::before {
    content: "▶";
    font-size: 0.6rem;
    margin-right: 0.875rem;
    transform: ${props => props.isOpen ? 'rotate(90deg)' : 'rotate(0)'};
    transition: transform 0.2s ease;
    color: rgba(0, 0, 0, 0.4);
  }
`;

// Common form styles
export const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  font-size: 0.9375rem;
  color: var(--brand-text-secondary);
`;

export const FormSection = styled.div`
  background: white;
  padding: 1.75rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  border: 1px solid var(--brand-line);
  margin-bottom: 1.5rem;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 6px 12px rgba(0,0,0,0.07);
  }
`;

export const FormRow = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const FormDescription = styled.p`
  font-size: 0.9rem;
  color: var(--brand-text-secondary);
  margin: 0.5rem 0 1rem;
  line-height: 1.5;
`;

export const ErrorMessage = styled.div`
  color: #f44336;
  font-size: 0.8125rem;
  margin-top: 0.375rem;
`;

// Common input styles
export const Input = styled.input`
  font-family: var(--font-family-text);
  font-size: var(--font-size-body);
  padding: var(--spacing-md);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  width: 100%;
  background: var(--color-background-primary);
  transition: all var(--transition-fast);

  &:focus {
    outline: none;
    border-color: var(--color-border-focus);
    box-shadow: 0 0 0 2px rgba(198, 167, 94, 0.1);
  }

  &::placeholder {
    font-family: var(--font-family-text);
    font-size: 0.9rem;
    letter-spacing: normal;
    color: var(--color-text-placeholder);
    font-weight: normal;
  }
`;

export const Select = styled.select`
  font-family: var(--font-family-text);
  font-size: var(--font-size-body);
  padding: var(--spacing-md);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  width: 100%;
  background: var(--color-background-primary);
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1em;
  transition: all var(--transition-fast);

  &:focus {
    outline: none;
    border-color: var(--color-border-focus);
    box-shadow: 0 0 0 2px rgba(198, 167, 94, 0.1);
  }
`;

export const TextArea = styled.textarea`
  font-family: var(--font-family-text);
  font-size: var(--font-size-body);
  padding: var(--spacing-md);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  width: 100%;
  background: var(--color-background-primary);
  resize: vertical;
  min-height: 80px;
  transition: all var(--transition-fast);

  &:focus {
    outline: none;
    border-color: var(--color-border-focus);
    box-shadow: 0 0 0 2px rgba(198, 167, 94, 0.1);
  }

  &::placeholder {
    font-family: var(--font-family-text);
    font-size: 0.9rem;
    letter-spacing: normal;
    color: var(--color-text-placeholder);
    font-weight: normal;
  }
`;

// Form layout elements
export const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

// Common button styles
export const Button = styled.button`
  font-family: var(--font-family-text);
  font-size: var(--font-size-small);
  font-weight: 500;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-light);
  background: var(--color-background-secondary);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--color-background-hover);
  }

  &:focus {
    outline: none;
  }
`;

export const IconButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
`;

// Button Variants
export const PrimaryButton = styled(Button)`
  background: var(--brand-gold);
  border-color: var(--brand-gold);
  color: white;

  &:hover {
    background: var(--brand-gold-hover);
    border-color: var(--brand-gold-hover);
  }
`;

export const SecondaryButton = styled(Button)`
  background: white;
  border-color: var(--brand-gold);
  color: var(--brand-gold);

  &:hover {
    background: rgba(198, 167, 94, 0.05);
  }
`;

export const TextButton = styled(Button)`
  background: transparent;
  border-color: transparent;
  color: var(--brand-text-secondary);


  &:hover {
    background: rgba(0, 0, 0, 0.05);
    border-color: transparent;
  }
`;

export const DangerButton = styled(Button)`
  background: #f44336;
  border-color: #f44336;
  color: white;

  &:hover {
    background: #d32f2f;
    border-color: #d32f2f;
  }
`;

export const ActionButton = styled(PrimaryButton)`
  font-size: var(--font-size-body);
  padding: var(--spacing-md) var(--spacing-lg);
`;

// Button Size Variants
export const SmallButton = styled(Button)`
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
`;

export const LargeButton = styled(Button)`
  font-size: 1rem;
  padding: 0.75rem 1.5rem;
`;
